import type {
  AllergenDto,
  CourseDto,
  MenuItemVariantDto,
  MenuSectionDto,
  MenuSubSectionDto,
  OptionDto,
  OptionGroupDto,
} from '../../generated-api';
import { OptionGroupDtoProductTypeEnum, OptionGroupDtoSelectionTypeEnum } from '../../generated-api';
import type {
  PlainAllergenDto,
  CourseDto as PlainCourseDto,
  PlainItemDto,
  PlainMenuDto,
  PlainOpeningHoursDto,
  PlainOptionDto,
  PlainOptionGroupDto,
  PlainSectionDto,
  PlainSubSectionDto,
  PlainVariantOptionDto,
  PlainVariantOptionGroupDto,
} from '@ordr-as/menu-builder-types/epos';

// Valid enum values for runtime validation
const VALID_SELECTION_TYPES = Object.values(OptionGroupDtoSelectionTypeEnum);
const VALID_PRODUCT_TYPES = Object.values(OptionGroupDtoProductTypeEnum);

function toSelectionType(value: string): OptionGroupDtoSelectionTypeEnum {
  if (VALID_SELECTION_TYPES.includes(value as OptionGroupDtoSelectionTypeEnum)) {
    return value as OptionGroupDtoSelectionTypeEnum;
  }
  // oxlint-disable-next-line no-console -- intentional runtime validation warning
  console.warn(`Invalid selectionType "${value}", defaulting to SINGLE_CHOICE`);
  return OptionGroupDtoSelectionTypeEnum.SINGLE_CHOICE;
}

function toProductType(value: string): OptionGroupDtoProductTypeEnum {
  if (VALID_PRODUCT_TYPES.includes(value as OptionGroupDtoProductTypeEnum)) {
    return value as OptionGroupDtoProductTypeEnum;
  }
  // oxlint-disable-next-line no-console -- intentional runtime validation warning
  console.warn(`Invalid productType "${value}", defaulting to ADDON`);
  return OptionGroupDtoProductTypeEnum.ADDON;
}

export interface MappedMenuData {
  activeSections: MenuSectionDto[];
  courses: CourseDto[];
  componentProducts: MenuItemVariantDto[];
  openingHours: Record<string, PlainOpeningHoursDto[]>;
  searchableSections: MenuSectionDto[];
}

export function mapPlainMenuToMenuData(plainMenu: PlainMenuDto): MappedMenuData {
  const searchableSections = [...plainMenu.sections, ...plainMenu.searchOnlySections];

  // Filter by active only - opening hours filtering is done at runtime via useActiveSections
  const activeSections = plainMenu.sections.filter((section) => section.active);
  const activeSectionsWithSearchOnly = searchableSections.filter((section) => section.active);

  // Collect opening hours for all sections for filtering at read time
  const openingHours: Record<string, PlainOpeningHoursDto[]> = {};
  for (const section of searchableSections) {
    if (section.hasOpeningHours && section.openingHours.length > 0) {
      openingHours[section.sectionId] = section.openingHours;
    }
  }

  return {
    activeSections: activeSections.map(mapPlainSectionToMenuSection),
    componentProducts: plainMenu.componentProducts.map(mapPlainItemToMenuItemVariant),
    courses: plainMenu.courses.map(mapCourse),
    openingHours,
    searchableSections: activeSectionsWithSearchOnly.map(mapPlainSectionToMenuSection),
  };
}

function mapCourse(course: PlainCourseDto): CourseDto {
  return {
    courseId: course.courseId ?? '',
    name: course.name,
    sequence: course.sequence ?? 0,
  };
}

function mapPlainSectionToMenuSection(section: PlainSectionDto): MenuSectionDto {
  return {
    eposDisplayColor: section.eposDisplayColor ?? undefined,
    items: (section.items ?? []).filter((item) => item.active).map(mapPlainItemToMenuItemVariant),
    locationId: section.locationId ?? 0,
    locationName: section.locationName,
    menuId: section.menuId,
    name: section.name,
    sectionId: section.sectionId,
    subsections: (section.subSections ?? [])
      .filter((subsection) => subsection.items.length > 0)
      .map(mapPlainSubsectionItemToMenuSubsection),
  };
}

function mapPlainItemToMenuItemVariant(item: PlainItemDto): MenuItemVariantDto {
  return {
    variantLinkId: item.variantLinkId,
    name: item.name,
    price: item.price ?? 0,
    takeawayPrice: item.takeawayPrice ?? undefined,
    description: item.description,
    allergens: item.allergens.map(mapPlainAllergenToAllergen),
    // Merge variant option groups (first) with regular option groups
    optionGroups: [
      ...(item.variantOptionGroups ?? []).map((vog) => mapVariantOptionGroup(vog)),
      ...(item.optionGroups ?? []).map(mapPlainOptionGroupToOptionGroup),
    ],
    menuSectionId: item.menuSectionId ?? undefined,
    sectionProductId: item.sectionProductId ?? undefined,
    eposDisplayColor: item.eposDisplayColor ?? undefined,
    allowOpenPrice: item.allowOpenPrice,
    courseId: item.courseId ?? undefined,
    courseName: item.courseName ?? undefined,
    courseSequence: item.courseSequence ?? undefined,
    giftCardId: item.giftCardId ?? undefined,
    giftCardExpirationYears: item.giftCardExpirationYears ?? undefined,
    priceType: transformPriceType(item),
    plu: item.plu ?? undefined,
    gtin: item.gtin ?? undefined,
    abv: item.abv ?? undefined,
    isComposite: item.isComposite ?? false,
    autoApplyBundlePrice: item.autoApplyBundlePrice ?? undefined,
    showComponents: item.showComponents ?? undefined,
    // PlainComponentDto[] and MenuBuilderComponentDto[] are structurally different — the generated API type doesn't match the runtime shape from menu-builder
    components: item.components as unknown as MenuItemVariantDto['components'],
  };
}

function mapPlainSubsectionItemToMenuSubsection(item: PlainSubSectionDto): MenuSubSectionDto {
  return {
    active: item.active ?? true,
    id: item.sectionId,
    items: (item.items ?? []).filter((subItem) => subItem.active).map(mapPlainItemToMenuItemVariant),
    name: item.name,
    productCount: item.items?.length ?? 0,
    sectionOwnerLocationId: 0,
    sectionOwnerName: '',
    sectionType: '',
    sequence: item.sectionSequence ?? 0,
  };
}

/**
 * Transform price type values to normalized enum format.
 * Server-side mapping logic replicated from V5 endpoint.
 */
function transformPriceType(item: PlainItemDto): string | undefined {
  if (item.allowOpenPrice) {
    return 'OPEN_PRICE';
  }

  const { priceType } = item;
  if (!priceType) {
    return 'PER_ITEM';
  }

  const mapping: Record<string, string> = {
    'Open price': 'OPEN_PRICE',
    'Per gram': 'PER_GRAM',
    'Per hg': 'PER_HG',
    'Per item': 'PER_ITEM',
    'Per kg': 'PER_KG',
  };

  return mapping[priceType] ?? priceType;
}

/**
 * Map variant option group to standard OptionGroupDto.
 * Variant option groups have a different structure and need special handling.
 */
function mapVariantOptionGroup(group: PlainVariantOptionGroupDto): OptionGroupDto {
  return {
    optionGroupId: undefined,
    variantLinkId: group.variantLinkId,
    selectionType: toSelectionType(group.selectionType),
    productType: toProductType(group.menuItemType),
    displayName: group.displayName ?? '',
    description: group.description ?? '',
    maxSelection: 0,
    minSelection: 0,
    required: group.required ?? false,
    // Use the group's variantLinkId (not item's) to determine preSelected - matches LocationV2Dto behavior
    options: (group.variantOptions ?? []).map((vo) => mapVariantOption(vo, group.variantLinkId)),
  };
}

/**
 * Map variant option to standard OptionDto.
 * preSelected is true when this variant is the "parent" variant (default selection).
 */
function mapVariantOption(option: PlainVariantOptionDto, parentVariantLinkId: string): OptionDto {
  return {
    name: option.name,
    optionId: option.variantId,
    preSelected: option.variantId === parentVariantLinkId,
    price: option.price ?? 0,
    takeawayPrice: option.takeawayPrice ?? undefined,
  };
}

function mapPlainAllergenToAllergen(allergen: PlainAllergenDto): AllergenDto {
  return {
    name: allergen.name,
    type: allergen.allergenType,
  };
}

function mapPlainOptionGroupToOptionGroup(group: PlainOptionGroupDto): OptionGroupDto {
  return {
    description: group.description ?? '',
    displayName: group.displayName,
    maxSelection: group.maxSelection ?? 0,
    minSelection: group.minSelection ?? 0,
    optionGroupId: group.optionGroupId,
    options: group.options.map(mapPlainOptionToOption),
    productType: toProductType(group.productType),
    required: group.required ?? false,
    selectionType: toSelectionType(group.selectionType),
    variantLinkId: group.variantLinkId,
  };
}

function mapPlainOptionToOption(option: PlainOptionDto): OptionDto {
  return {
    name: option.name,
    optionId: option.optionId,
    preSelected: option.preSelected ?? false,
    price: option.price ?? 0,
  };
}
